import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import KPICard from '../../components/primitives/KPICard';
import Panel from '../../components/primitives/Panel';
import StatusPill from '../../components/primitives/StatusPill';
import { PGLineChart } from '../../components/primitives/PGChart';
import { getBranch } from '../../utils/orgData';
import { PG } from '../../utils/theme';

const { FiAlertOctagon, FiShield, FiBriefcase, FiLock, FiCheckCircle } = FiIcons;

const PPE_ITEMS = [
  'Safety goggles',
  'Cut-resistant gloves',
  'Steel-toe boots',
  'Hi-vis vest',
  'Respirator (paint zone)',
  'Hearing protection',
  'First-aid kit stocked',
  'Eye-wash station',
];

const NEAR_MISS = [
  { id: 'NM-041', title: 'Glass sheet slipped during handling', reporter: 'T. Mokoena', ago: '2d', tone: 'warn' },
  { id: 'NM-042', title: 'Spilt primer on workshop floor', reporter: 'R. Davids', ago: '5d', tone: 'warn' },
  { id: 'NM-043', title: 'Unmarked reverse area — delivery truck', reporter: 'Branch Mgr', ago: '9d', tone: 'alert' },
];

const INSURER_AUDITS = [
  { date: '2026-04-10', insurer: 'Santam', result: 'Pass', tone: 'ok' },
  { date: '2026-03-02', insurer: 'Discovery', result: 'Pass w/ obs', tone: 'warn' },
  { date: '2026-01-22', insurer: 'Outsurance', result: 'Pass', tone: 'ok' },
  { date: '2025-12-14', insurer: 'Hollard', result: 'Pass', tone: 'ok' },
];

function observations(seed) {
  const out = [];
  let v = 3;
  for (let i = 0; i < 30; i++) {
    v = Math.max(0, Math.min(10, v + (Math.sin(i * 0.5 + seed) * 2)));
    out.push(Math.round(v));
  }
  return out;
}

export default function ComplianceBranchView() {
  const { branchId } = useParams();
  const branch = getBranch(branchId);

  const obs = useMemo(() => observations((branch?.id ?? '').length), [branch]);

  if (!branch) {
    return <div className="h-full flex items-center justify-center text-neutral-500 font-mono-tech text-xs uppercase tracking-widest">Branch not found</div>;
  }

  // Generate PPE statuses: mostly green with 1-2 red
  const ppeStatus = PPE_ITEMS.map((_, i) => {
    const seed = (i * 7 + branch.id.length) % 10;
    return seed < 2 ? 'alert' : 'ok';
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="h-full flex flex-col gap-4 min-h-0"
    >
      <div className="flex items-baseline justify-between flex-shrink-0">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-neutral-500">Compliance / Branch drilldown</div>
          <div className="flex items-baseline gap-3 mt-1">
            <h1 className="font-doto text-3xl text-white">{branch.name}</h1>
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-neutral-500">{branch.town} &middot; {branch.country}</span>
          </div>
        </div>
        <StatusPill tone={branch.compliance.ppeCompliance > 95 ? 'ok' : 'warn'}>Peer: {branch.peerGroup}</StatusPill>
      </div>

      <div className="grid grid-cols-5 gap-3 flex-shrink-0">
        <KPICard title="NEAR MISSES" value={branch.compliance.safetyNearMisses} icon={FiAlertOctagon} accent={PG.amber} />
        <KPICard title="PPE COMPLIANCE" value={branch.compliance.ppeCompliance} unit="%" icon={FiShield} accent={PG.emerald} target=">95%" />
        <KPICard title="LEGAL OPEN" value={branch.compliance.legalDisputesOpen} icon={FiBriefcase} accent={branch.compliance.legalDisputesOpen > 0 ? PG.amber : PG.emerald} />
        <KPICard title="POPIA INCIDENTS" value={branch.compliance.popiaIncidents} icon={FiLock} accent={branch.compliance.popiaIncidents > 0 ? PG.red : PG.emerald} />
        <KPICard title="INSURER AUDIT" value={branch.compliance.insurerAuditPass} unit="%" icon={FiCheckCircle} accent={PG.emerald} />
      </div>

      <div className="grid grid-cols-12 gap-4 flex-1 min-h-0">
        <Panel title="SAFETY_OBSERVATIONS_30D" subtitle="daily count" className="col-span-7">
          <div className="h-full p-2">
            <PGLineChart
              categories={Array.from({ length: 30 }, (_, i) => `D${i + 1}`)}
              series={[{ name: 'Observations', data: obs, itemStyle: { color: PG.amber }, lineStyle: { color: PG.amber, width: 2 } }]}
            />
          </div>
        </Panel>

        <Panel title="OPEN_NEAR_MISSES" subtitle={`${NEAR_MISS.length} pending`} className="col-span-5">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {NEAR_MISS.map((n) => (
              <div key={n.id} className="px-4 py-3 border-b border-white/5">
                <div className="flex justify-between items-baseline">
                  <span className="font-mono-tech text-[10px] text-neutral-400">{n.id}</span>
                  <StatusPill tone={n.tone}>{n.ago} old</StatusPill>
                </div>
                <div className="text-white text-sm mt-1">{n.title}</div>
                <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500 mt-1">Reporter: {n.reporter}</div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="INSURER_AUDIT_TIMELINE" subtitle="last 4 audits" className="col-span-7">
          <div className="h-full p-4 flex flex-col justify-center">
            <div className="relative">
              <div className="absolute left-0 right-0 top-3 h-px bg-white/10" />
              <div className="flex justify-between relative">
                {INSURER_AUDITS.map((a, i) => (
                  <div key={i} className="flex flex-col items-center gap-2">
                    <div
                      className="w-6 h-6 rounded-full border-2 bg-black flex-shrink-0"
                      style={{ borderColor: a.tone === 'ok' ? PG.emerald : PG.amber }}
                    />
                    <div className="text-center">
                      <div className="font-mono-tech text-[9px] uppercase tracking-widest text-neutral-500">{a.date}</div>
                      <div className="text-white text-sm mt-1">{a.insurer}</div>
                      <div className="font-mono-tech text-[10px] mt-0.5" style={{ color: a.tone === 'ok' ? PG.emerald : PG.amber }}>{a.result}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Panel>

        <Panel title="PPE_AUDIT_CHECKLIST" subtitle="daily" className="col-span-5">
          <div className="h-full overflow-y-auto custom-scrollbar">
            {PPE_ITEMS.map((item, i) => (
              <div key={item} className="flex items-center justify-between px-4 py-2 border-b border-white/5">
                <span className="text-sm text-neutral-300">{item}</span>
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{
                    backgroundColor: ppeStatus[i] === 'ok' ? PG.emerald : PG.red,
                    boxShadow: `0 0 6px ${ppeStatus[i] === 'ok' ? PG.emerald : PG.red}88`,
                  }}
                />
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </motion.div>
  );
}
